<script lang="ts">
	import { browser } from '$app/environment';
	import { cfgBrandTitle, cfgToThemeContentPath } from '@infinitro/config';
	import { Verbiage, t } from '@infinitro/content';
	import { LeftIcon } from '@infinitro/icons';
	import { Spinner } from '@infinitro/loading';
	import { locale } from '@infinitro/global';
	import MarkdownIt from 'markdown-it';
	import { supportChatWarn } from '../debug';
	import { normalizeLocale } from '../utils/support-chat-article-tree';

	export let articleTitle = '';
	export let articleSlug = '';
	export let onBack: () => void = () => {};

	let htmlContent = '';
	let isLoading = false;
	let loadError = false;

	const brandTitle = cfgBrandTitle();

	const md = new MarkdownIt({
		html: true,
		breaks: true,
		linkify: true
	});
	const defaultNormalizeLink = md.normalizeLink.bind(md);
	md.normalizeLink = (url: string) => defaultNormalizeLink(url).replace(/^http:\/\//, 'https://');

	async function fetchArticleMd(localeCode: string): Promise<string> {
		const url = cfgToThemeContentPath(`/support-chat/${articleSlug}.${localeCode}.md`);
		const res = await fetch(url);
		if (!res.ok) throw new Error(String(res.status));
		let text = await res.text();
		text = text.replace(/%(domain|brand)%/gi, brandTitle);
		return text;
	}

	async function loadArticle() {
		if (!browser || !articleSlug.trim()) return;

		isLoading = true;
		loadError = false;
		htmlContent = '';

		const primaryLocale = normalizeLocale($locale);

		let lastError = false;

		try {
			const localesToTry = primaryLocale === 'en' ? ['en'] : [primaryLocale, 'en'];

			for (let i = 0; i < localesToTry.length; i++) {
				const loc = localesToTry[i];
				try {
					const raw = await fetchArticleMd(loc);
					htmlContent = md.render(raw);
					isLoading = false;
					return;
				} catch {
					lastError = true;
				}
			}
		} catch {
			lastError = true;
		}

		if (lastError) {
			supportChatWarn('Support chat article markdown fetch failed', {
				slug: articleSlug,
				locale: normalizeLocale($locale)
			});
			loadError = true;
			htmlContent = '';
		}
		isLoading = false;
	}

	$: if (browser && articleSlug.trim()) {
		$locale;
		void loadArticle();
	}
</script>

<section class="screen-article">
	<div class="screen-topbar">
		<button class="back-button" type="button" on:click={onBack} aria-label={$t(Verbiage.back)}>
			<span class="back-icon"><LeftIcon /></span>
		</button>
		<h2 class="screen-title">{articleTitle}</h2>
		<span class="screen-title-spacer"></span>
	</div>

	<div class="screen-body">
		{#if isLoading}
			<div class="state-center" role="status" aria-live="polite">
				<span class="sr-only">{$t(Verbiage.loading)}</span>
				<Spinner size="40px" color="var(--color-text-500)" speed="0.9s" />
			</div>
		{:else if loadError}
			<p class="state-muted">{$t(Verbiage.support_chat_no_messages)}</p>
		{:else}
			<div class="article-html">{@html htmlContent}</div>
		{/if}
	</div>
</section>

<style>
	.screen-article {
		display: flex;
		height: 100%;
		flex-direction: column;
		background-color: var(--color-text-100);
}

	.screen-topbar {
		display: grid;
		grid-template-columns: 32px 1fr 32px;
		align-items: center;
		background-color: var(--color-text-100);
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.25rem;
		padding-bottom: 0.25rem;
}

	.screen-title {
		overflow: hidden;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		text-align: center;
		font-size: 1rem;
		line-height: 1.5rem;
		font-weight: 600;
		line-height: 1.375;
		color: var(--color-text-900);
}

	.screen-title-spacer {
		display: block;
		height: 2rem;
		width: 2rem;
}

	.back-button {
		display: flex;
		height: 2rem;
		width: 2rem;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		border-radius: 9999px;
		transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 150ms;
		color: var(--color-text-900);
		background-color: rgba(var(--color-background-900-rgb), 0.1);
}

	.back-button:hover {
		background-color: rgba(var(--color-background-900-rgb), 0.16);
	}

	.back-icon {
		display: block;
		height: 1rem;
		width: 1rem;
		flex-shrink: 0;
		color: var(--color-text-900);
		filter: brightness(0.45);
}

	.screen-body {
		min-height: 0px;
		flex: 1 1 0%;
		overflow-y: auto;
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.75rem;
		padding-bottom: 0.75rem;
}

	.state-center {
		display: flex;
		flex: 1 1 0%;
		align-items: center;
		justify-content: center;
		padding-top: 2.5rem;
		padding-bottom: 2.5rem;
}

	.state-muted {
		padding-left: 0.25rem;
		padding-right: 0.25rem;
		padding-top: 1.25rem;
		padding-bottom: 1.25rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--color-text-500);
}

	.article-html {
		max-width: none;
		font-size: 0.875rem;
		line-height: 1.25rem;
		line-height: 1.375;
		color: var(--color-text-900);
}

	.article-html :global(p) {
		margin-bottom: 0.75rem;
}

	.article-html :global(ul),
	.article-html :global(ol) {
		margin-bottom: 0.75rem;
		margin-left: 1.25rem;
		list-style-type: disc;
}

	.article-html :global(a) {
		color: var(--color-secondary-500);
	}

	.article-html :global(table) {
		margin-bottom: 1rem;
		display: block;
		width: 100%;
		overflow-x: auto;
		border-radius: 0.75rem;
		border-width: 1px;
		border-style: solid;
		font-size: 0.75rem;
		line-height: 1rem;
		border-color: rgba(var(--color-text-900-rgb), 0.12);
		border-spacing: 0;
		background-color: rgba(var(--color-text-100-rgb), 0.78);
}

	.article-html :global(thead) {
		background-color: rgba(var(--color-text-900-rgb), 0.06);
	}

	.article-html :global(th),
	.article-html :global(td) {
		min-width: 112px;
		white-space: normal;
		padding-left: 0.75rem;
		padding-right: 0.75rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		text-align: left;
		vertical-align: top;
		line-height: 1.375;
		border-bottom: 1px solid var(--color-text-300);
}

	.article-html :global(th) {
		font-weight: 600;
		color: var(--color-text-900);
}

	.article-html :global(td) {
		color: var(--color-text-700);
	}

	.article-html :global(tbody tr:last-child td) {
		border-bottom: 0;
	}

	.article-html :global(tbody tr:nth-child(even)) {
		background-color: rgba(var(--color-text-900-rgb), 0.035);
	}

	.article-html :global(h1),
	.article-html :global(h2),
	.article-html :global(h3) {
		margin-bottom: 0.5rem;
		font-weight: 600;
		color: var(--color-text-900);
}

	.back-icon :global(svg),
	.back-icon :global(img) {
		height: 100%;
		width: 100%;
}

	.back-icon :global(path),
	.back-icon :global(polyline),
	.back-icon :global(line) {
		stroke: currentColor;
	}

	.sr-only {
		position: absolute;
		height: 1px;
		width: 1px;
		overflow: hidden;
		white-space: nowrap;
		padding: 0px;
		clip: rect(0, 0, 0, 0);
		clip-path: inset(50%);
		margin: -1px;
		border: 0;
}
</style>
